import React, {
  FunctionComponent,
  useState,
  cloneElement,
  Children,
  Fragment,
  HTMLAttributes,
  isValidElement,
  useCallback,
  useLayoutEffect,
  PropsWithChildren,
  ReactNode,
} from 'react';
import Backdrop from './backdrop';
import Portal from '../../@_components/portal';
import useBodyScrollLock from '../../@hooks/use-body-scroll-lock';
import useEscape from '../../@hooks/use-escape';
import useFocusTrap from '../../@hooks/use-focus-trap';
import useOnClickOutside from '../../@hooks/use-on-click-outside';

interface Props {
  clickOutsideElement?: HTMLElement | null;
  closeOnClickOutside?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const DIALOG_ATTRIBUTES = {'role': 'dialog', 'aria-modal': true} as const;

type DialogElementProps = HTMLAttributes<HTMLElement> & {
  ref: React.Ref<any>;
};

const Modal: FunctionComponent<PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
  clickOutsideElement,
  closeOnClickOutside = true,
  children,
}) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [hasOwnDialog, setHasOwnDialog] = useState(false);

  // While a closing dialog animates out, the Dialogs render it beside the
  // one opening; both carry this ref, so the leaving one's detach must not
  // drop the container that is still on screen.
  const attachContainer = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      setContainer(element);

      return;
    }

    setContainer((current) =>
      current && current.isConnected ? current : null
    );
  }, []);

  const handleClose = useCallback(() => {
    if (!isOpen) return;
    onClose();
  }, [isOpen, onClose]);

  const handleClickOutside = useCallback(() => {
    if (!closeOnClickOutside) return;
    handleClose();
  }, [closeOnClickOutside, handleClose]);

  useBodyScrollLock(isOpen);
  useOnClickOutside(clickOutsideElement || container, handleClickOutside);
  useEscape(handleClose);
  useFocusTrap(container, isOpen);

  // Content that is already a labelled dialog of its own keeps that role,
  // and the element around it stays plain, so dialogs never nest. That
  // inner dialog is still modal, so it gets aria-modal when it lacks it.
  useLayoutEffect(() => {
    const ownDialog = container?.querySelector('[role="dialog"]');

    setHasOwnDialog(!!ownDialog);

    if (ownDialog && !ownDialog.hasAttribute('aria-modal')) {
      ownDialog.setAttribute('aria-modal', 'true');
    }
  }, [container]);

  // A Fragment cannot hold a ref or attributes (the Dialogs pass one, from
  // their transition), so its children are the dialog elements.
  const asDialog = (child: ReactNode): ReactNode => {
    if (!isValidElement<DialogElementProps>(child)) return;

    if (child.type === Fragment) {
      return Children.map(child.props.children, asDialog);
    }

    return cloneElement(child, {
      ref: attachContainer,
      ...(!hasOwnDialog && DIALOG_ATTRIBUTES),
    });
  };

  return (
    <Portal>
      <Backdrop isOpen={isOpen} />

      {Children.map(children, asDialog)}
    </Portal>
  );
};

export default Modal;
